import React, { useState } from 'react';
import { Button } from '../ui/button';
import axios from 'axios';
import { API } from './shared';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', organization: '', role: '', industry: '', urgency: '', company_size: '', referral_source: '', challenge_description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [matchedCases, setMatchedCases] = useState([]);
  const [error, setError] = useState(null);

  const matchTypeLabels = { best_fit: 'Most Relevant', analogous: 'Related Evidence', credibility: 'Additional Proof Point' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/submit-challenge`, { ...formData, challenge_type: formData.industry || 'general' });
      if (response.data.status === 'success') {
        setSubmitted(true);
        if (response.data.matched_cases) setMatchedCases(response.data.matched_cases);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-spacing cta-section" data-testid="contact-section">
      <div className="container-luxury">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <p className="overline mb-4">Take Action</p>
          <p className="text-[#64748B] text-sm leading-relaxed mb-6 italic">
            The cost of guessing: the wrong bet gets funded, a competitor takes the shift first, and the stall gets explained to the board.
          </p>
          <h2 className="heading-section text-4xl sm:text-5xl text-[#0B1A2B] mb-6" data-testid="contact-headline">
            Bring us the bet you are about to make.
          </h2>
          <p className="body-large">
            Tell us the decision in front of you. We'll reply about whether and how we can help. No obligation either way.
          </p>
        </div>

        {/* Prominent Book a Meeting CTA */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-[#0B1A2B] p-8 sm:p-10 text-center">
            <h3 className="font-serif text-2xl text-white mb-3">Let's Talk</h3>
            <p className="text-[#94A3B8] text-sm mb-6">Pick a time. 20 minutes, no slides. We'll listen to what you're working through and tell you straight whether we can help.</p>
            <a
              href="https://calendly.com/forest-harpercookconsultingllc/book-time-with-me?month=2026-10"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#D97706] text-white px-10 py-4 text-sm font-medium tracking-wide hover:bg-[#B45309] transition-colors"
              data-testid="book-meeting-btn"
            >
              Book a 20-Minute Call
            </a>
          </div>
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-[#E2E8F0]" />
            <span className="text-xs text-[#94A3B8] uppercase tracking-wider">or submit your challenge below</span>
            <div className="flex-1 h-px bg-[#E2E8F0]" />
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
          {submitted ? (
            <div className="bg-white border border-[#E2E8F0] p-8 sm:p-12">
              <div className="text-center mb-8">
                <div className="w-16 h-16 border-2 border-[#D97706] flex items-center justify-center mx-auto mb-6">
                  <span className="text-[#D97706] text-2xl">&#10003;</span>
                </div>
                <h3 className="text-2xl font-serif text-[#0B1A2B] mb-3">Challenge Received</h3>
                <p className="text-[#334155] max-w-lg mx-auto">
                  Our leadership team will personally review your submission and reach out within 48 hours.
                  {formData.email && <span className="block mt-2 text-sm text-[#64748B]">We've also sent relevant case evidence to your email.</span>}
                </p>
              </div>

              {matchedCases.length > 0 && (
                <div className="mt-8 pt-8 border-t border-[#E2E8F0]" data-testid="matched-cases">
                  <p className="text-xs text-[#D97706] uppercase tracking-wider font-medium mb-6 text-center">Relevant Work From Our Case Library</p>
                  <div className="space-y-4">
                    {matchedCases.map((mc, i) => (
                      <div key={i} className="p-6 bg-[#F8FAFC] border-l-[3px] border-[#D97706]" data-testid={`matched-case-${i + 1}`}>
                        <span className="text-[10px] text-[#D97706] uppercase tracking-wider font-medium">{matchTypeLabels[mc.match_type] || 'Relevant'}</span>
                        <h4 className="font-serif text-lg text-[#0B1A2B] mt-1 mb-2">{mc.title}</h4>
                        <p className="text-sm text-[#334155] mb-2">{mc.reason}</p>
                        <p className="text-xs text-[#64748B] italic">{mc.quantified_outcomes}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <a href={`${API}/resources/apig-firm-overview.pdf`} target="_blank" rel="noopener noreferrer" className="btn-primary rounded-none inline-flex items-center justify-center px-8 py-4">
                  Download APIG Overview
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] p-8 sm:p-12">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-6">{error}</div>}
              <div className="form-row mb-5">
                <div className="form-group">
                  <input type="text" placeholder="Your Name *" className="input-editorial" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="input-name" />
                </div>
                <div className="form-group">
                  <input type="email" placeholder="Email Address *" className="input-editorial" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="input-email" />
                </div>
              </div>
              <div className="form-row mb-5">
                <div className="form-group">
                  <input type="text" placeholder="Organization" className="input-editorial" value={formData.organization} onChange={(e) => setFormData({...formData, organization: e.target.value})} data-testid="input-organization" />
                </div>
                <div className="form-group">
                  <input type="text" placeholder="Your Role / Title" className="input-editorial" value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} data-testid="input-role" />
                </div>
              </div>
              <div className="form-row mb-5">
                <div className="form-group">
                  <select className="input-editorial" value={formData.industry} onChange={(e) => setFormData({...formData, industry: e.target.value})} data-testid="input-industry">
                    <option value="">Industry</option>
                    <option value="healthcare-medtech-pharma">Healthcare / MedTech / Pharma</option>
                    <option value="retail-consumer-cpg">Retail / Consumer / CPG</option>
                    <option value="financial-services">Financial Services / Insurance</option>
                    <option value="media-entertainment">Media / Entertainment</option>
                    <option value="energy-industrial">Energy / Industrial</option>
                    <option value="private-equity">Private Equity</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <select className="input-editorial" value={formData.urgency} onChange={(e) => setFormData({...formData, urgency: e.target.value})} data-testid="input-urgency">
                    <option value="">When do you need to act?</option>
                    <option value="this-quarter">This quarter</option>
                    <option value="next-6-months">Next 6 months</option>
                    <option value="exploring">Exploring options</option>
                  </select>
                </div>
              </div>
              <div className="form-row mb-5">
                <div className="form-group">
                  <select className="input-editorial" value={formData.company_size} onChange={(e) => setFormData({...formData, company_size: e.target.value})} data-testid="input-company-size">
                    <option value="">Company Size (Revenue)</option>
                    <option value="under-50m">Under $50M</option>
                    <option value="50m-250m">$50M – $250M</option>
                    <option value="250m-1b">$250M – $1B</option>
                    <option value="over-1b">Over $1B</option>
                  </select>
                </div>
                <div className="form-group">
                  <select className="input-editorial" value={formData.referral_source} onChange={(e) => setFormData({...formData, referral_source: e.target.value})} data-testid="input-referral">
                    <option value="">How did you hear about APIG?</option>
                    <option value="referral">Referral</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="search">Search</option>
                    <option value="event">Event / Conference</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <textarea placeholder="Describe your strategic challenge... *" className="input-editorial min-h-[120px] resize-none" value={formData.challenge_description} onChange={(e) => setFormData({...formData, challenge_description: e.target.value})} rows={4} required data-testid="input-message" />
                <div className="mt-3 p-4 bg-[#F8FAFC] border border-[#E2E8F0]">
                  <p className="text-xs font-medium text-[#0B1A2B] mb-2">Not sure where to start? Consider framing around:</p>
                  <ul className="text-xs text-[#64748B] space-y-1 list-none">
                    <li>What decision or bet are you trying to de-risk?</li>
                    <li>What is the market or competitive pressure driving urgency?</li>
                    <li>Where is the gap — insight, positioning, or execution?</li>
                    <li>What does success look like in 6-12 months?</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Button type="submit" disabled={isSubmitting} className="btn-primary rounded-none flex-1" data-testid="submit-button">
                  {isSubmitting ? 'Submitting...' : 'Submit Your Challenge'}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
